import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Stethoscope, Plus, Calendar as CalendarIcon, Trash2, Pill, Clock, AlertCircle, Sparkles, Loader2, Bell, Check, X, MapPin, Car } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const appointmentTypes = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'dentist', label: 'Dentist' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'specialist', label: 'Specialist' },
];

const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aptDialogOpen, setAptDialogOpen] = useState(false);
  const [medDialogOpen, setMedDialogOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    member_name: '',
    appointment_type: 'doctor',
    date: null,
    time: '',
    doctor_name: '',
    location: '',
    follow_up_needed: false,
    notes: ''
  });
  const [newMedicine, setNewMedicine] = useState({
    member_name: '',
    medicine_name: '',
    dosage: '',
    frequency: '',
    refill_date: null,
    notes: ''
  });
  
  // E7 Task E: AI Appointment Reminder state
  const [aiReminder, setAiReminder] = useState(null);
  const [aiReminderLoading, setAiReminderLoading] = useState(null); // Stores appointment ID being processed
  const [aiCallId, setAiCallId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aptRes, medRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/medicines`)
      ]);
      setAppointments(aptRes.data);
      setMedicines(medRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAptSubmit = async (e) => {
    e.preventDefault();
    try {
      const aptData = {
        ...newAppointment,
        date: newAppointment.date ? format(newAppointment.date, 'yyyy-MM-dd') : null
      };
      await axios.post(`${API}/appointments`, aptData);
      toast.success('Appointment added!');
      setAptDialogOpen(false);
      setNewAppointment({
        member_name: '', appointment_type: 'doctor', date: null, time: '',
        doctor_name: '', location: '', follow_up_needed: false, notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add appointment');
    }
  };

  const handleMedSubmit = async (e) => {
    e.preventDefault();
    try {
      const medData = {
        ...newMedicine,
        refill_date: newMedicine.refill_date ? format(newMedicine.refill_date, 'yyyy-MM-dd') : null
      };
      await axios.post(`${API}/medicines`, medData);
      toast.success('Medicine added!');
      setMedDialogOpen(false);
      setNewMedicine({
        member_name: '', medicine_name: '', dosage: '', frequency: '', refill_date: null, notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add medicine');
    }
  };

  const deleteAppointment = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success('Appointment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const deleteMedicine = async (id) => {
    try {
      await axios.delete(`${API}/medicines/${id}`);
      toast.success('Medicine deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // E7 Task E: Get AI reminder for appointment
  const getAIReminder = async (appointment) => {
    setAiReminderLoading(appointment.id);
    setAiCallId(null);
    try {
      const response = await axios.post(`${API}/ai/appointments/reminder`, {
        appointment: {
          title: `${appointment.member_name} - ${appointment.appointment_type}`,
          date: appointment.date,
          time: appointment.time,
          location: appointment.location,
          type: appointment.appointment_type,
          notes: appointment.notes
        }
      });
      setAiReminder({ ...response.data.reminder, appointmentId: appointment.id });
      setAiCallId(response.data.call_id || null);
    } catch (error) {
      toast.error('Failed to generate reminder');
    } finally {
      setAiReminderLoading(null);
    }
  };

  // HITL: Confirm AI action
  const confirmAIAction = async (userAction) => {
    if (!aiCallId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: aiCallId,
        user_action: userAction,
        edit_delta: []
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Dismiss AI reminder
  const dismissAIReminder = async () => {
    await confirmAIAction('ignored');
    setAiReminder(null);
    setAiCallId(null);
  };

  // Acknowledge reminder
  const acknowledgeReminder = async () => {
    await confirmAIAction('accepted');
    toast.success('Reminder noted!');
    setAiReminder(null);
    setAiCallId(null);
  };

  const upcomingAppointments = appointments.filter(a => 
    a.date && new Date(a.date) >= new Date()
  ).sort((a, b) => new Date(a.date) - new Date(b.date));

  const needsRefill = medicines.filter(m => 
    m.refill_date && new Date(m.refill_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-purple-600" />
            </div>
            Appointments & Meds
          </h1>
          <p className="text-nurture-muted mt-1">Track medical appointments and medications</p>
        </div>
      </div>

      {/* Alert for upcoming refills */}
      {needsRefill.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-amber-50 border-amber-200 rounded-3xl">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-amber-800">
                <strong>{needsRefill.length}</strong> medicine(s) need refill soon
              </span>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <Tabs defaultValue="appointments" className="space-y-6">
        <TabsList className="bg-nurture-cream-dark rounded-full p-1">
          <TabsTrigger value="appointments" className="rounded-full data-[state=active]:bg-white px-6" data-testid="tab-appointments">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Appointments
            {upcomingAppointments.length > 0 && (
              <Badge className="ml-2 bg-purple-100 text-purple-700">{upcomingAppointments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="medicines" className="rounded-full data-[state=active]:bg-white px-6" data-testid="tab-medicines">
            <Pill className="w-4 h-4 mr-2" />
            Medicines
            {medicines.length > 0 && (
              <Badge className="ml-2 bg-blue-100 text-blue-700">{medicines.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Appointments Tab */}
        <TabsContent value="appointments">
          <div className="flex justify-end mb-4">
            <Dialog open={aptDialogOpen} onOpenChange={setAptDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-appointment-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Appointment
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Appointment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAptSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Family Member</Label>
                      <Input
                        value={newAppointment.member_name}
                        onChange={(e) => setNewAppointment({ ...newAppointment, member_name: e.target.value })}
                        placeholder="Emma"
                        required
                        className="rounded-xl"
                        data-testid="apt-member-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={newAppointment.appointment_type} onValueChange={(v) => setNewAppointment({ ...newAppointment, appointment_type: v })}>
                        <SelectTrigger className="rounded-xl" data-testid="apt-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {appointmentTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="apt-date">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {newAppointment.date ? format(newAppointment.date, 'MMM d, yyyy') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newAppointment.date}
                            onSelect={(date) => setNewAppointment({ ...newAppointment, date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input
                        value={newAppointment.time}
                        onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })}
                        placeholder="10:00 AM"
                        className="rounded-xl"
                        data-testid="apt-time"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Doctor/Provider</Label>
                    <Input
                      value={newAppointment.doctor_name}
                      onChange={(e) => setNewAppointment({ ...newAppointment, doctor_name: e.target.value })}
                      placeholder="Dr. Smith"
                      className="rounded-xl"
                      data-testid="apt-doctor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={newAppointment.location}
                      onChange={(e) => setNewAppointment({ ...newAppointment, location: e.target.value })}
                      placeholder="City Medical Center"
                      className="rounded-xl"
                      data-testid="apt-location"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newAppointment.follow_up_needed}
                      onCheckedChange={(v) => setNewAppointment({ ...newAppointment, follow_up_needed: v })}
                      data-testid="apt-followup"
                    />
                    <Label>Follow-up needed</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={newAppointment.notes}
                      onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                      placeholder="Any notes..."
                      className="rounded-xl"
                      data-testid="apt-notes"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="apt-submit">
                    Add Appointment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-nurture-cream-dark rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : appointments.length === 0 ? (
            <Card className="rounded-3xl shadow-nurture">
              <CardContent className="py-16 text-center">
                <CalendarIcon className="w-16 h-16 mx-auto text-nurture-muted/30 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-nurture-navy mb-2">No appointments</h3>
                <p className="text-nurture-muted">Add your family's medical appointments</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {appointments.map((apt) => (
                  <motion.div
                    key={apt.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <Card className="rounded-3xl shadow-nurture group">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <Stethoscope className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-bold text-nurture-navy">{apt.member_name}</h3>
                              <Badge variant="outline" className="capitalize">{apt.appointment_type}</Badge>
                              {apt.follow_up_needed && (
                                <Badge className="bg-amber-100 text-amber-700">Follow-up</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-nurture-muted">
                              {apt.date && (
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="w-4 h-4" />
                                  {format(new Date(apt.date), 'MMM d, yyyy')}
                                </span>
                              )}
                              {apt.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {apt.time}
                                </span>
                              )}
                            </div>
                            {apt.doctor_name && (
                              <p className="text-sm text-nurture-navy mt-1">{apt.doctor_name}</p>
                            )}
                            {apt.location && (
                              <p className="text-sm text-nurture-muted">{apt.location}</p>
                            )}
                            
                            {/* E7 Task E: AI Reminder for this appointment */}
                            {aiReminder && aiReminder.appointmentId === apt.id && (
                              <motion.div 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-3 p-3 bg-purple-50 rounded-xl border border-purple-200"
                              >
                                <div className="flex items-start gap-2 mb-2">
                                  <Bell className="w-4 h-4 text-purple-600 mt-0.5" />
                                  <p className="text-sm text-purple-800">{aiReminder.reminder_text}</p>
                                </div>
                                {aiReminder.prep_items?.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-purple-700 mb-1">Things to prepare:</p>
                                    <ul className="text-xs text-purple-600 space-y-0.5">
                                      {aiReminder.prep_items.map((item, i) => (
                                        <li key={i} className="flex items-center gap-1">
                                          <Check className="w-3 h-3" /> {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {aiReminder.suggested_departure && (
                                  <div className="flex items-center gap-1 text-xs text-purple-700 mb-2">
                                    <Car className="w-3 h-3" />
                                    <span>Leave by {aiReminder.suggested_departure}</span>
                                    {aiReminder.travel_buffer_mins && (
                                      <span className="text-purple-500">({aiReminder.travel_buffer_mins} min buffer)</span>
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={acknowledgeReminder} className="h-7 text-xs bg-purple-500 hover:bg-purple-600">
                                    <Check className="w-3 h-3 mr-1" /> Got it
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={dismissAIReminder} className="h-7 text-xs">
                                    <X className="w-3 h-3 mr-1" /> Dismiss
                                  </Button>
                                </div>
                              </motion.div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            {/* E7: AI Reminder Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => getAIReminder(apt)}
                              disabled={aiReminderLoading === apt.id}
                              className="opacity-0 group-hover:opacity-100 text-purple-500 hover:text-purple-700"
                              title="Get AI reminder"
                              data-testid={`ai-remind-${apt.id}`}
                            >
                              {aiReminderLoading === apt.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteAppointment(apt.id)}
                              className="opacity-0 group-hover:opacity-100 text-nurture-muted hover:text-red-500"
                              data-testid={`delete-apt-${apt.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>

        {/* Medicines Tab */}
        <TabsContent value="medicines">
          <div className="flex justify-end mb-4">
            <Dialog open={medDialogOpen} onOpenChange={setMedDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-medicine-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Medicine
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Medicine</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleMedSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Family Member</Label>
                      <Input
                        value={newMedicine.member_name}
                        onChange={(e) => setNewMedicine({ ...newMedicine, member_name: e.target.value })}
                        placeholder="Emma"
                        required
                        className="rounded-xl"
                        data-testid="med-member-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Medicine Name</Label>
                      <Input
                        value={newMedicine.medicine_name}
                        onChange={(e) => setNewMedicine({ ...newMedicine, medicine_name: e.target.value })}
                        placeholder="Vitamin D"
                        required
                        className="rounded-xl"
                        data-testid="med-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dosage</Label>
                      <Input
                        value={newMedicine.dosage}
                        onChange={(e) => setNewMedicine({ ...newMedicine, dosage: e.target.value })}
                        placeholder="500mg"
                        className="rounded-xl"
                        data-testid="med-dosage"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Input
                        value={newMedicine.frequency}
                        onChange={(e) => setNewMedicine({ ...newMedicine, frequency: e.target.value })}
                        placeholder="Once daily"
                        className="rounded-xl"
                        data-testid="med-frequency"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Refill Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="med-refill-date">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {newMedicine.refill_date ? format(newMedicine.refill_date, 'MMM d, yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newMedicine.refill_date}
                          onSelect={(date) => setNewMedicine({ ...newMedicine, refill_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={newMedicine.notes}
                      onChange={(e) => setNewMedicine({ ...newMedicine, notes: e.target.value })}
                      placeholder="Take with food..."
                      className="rounded-xl"
                      data-testid="med-notes"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="med-submit">
                    Add Medicine
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-nurture-cream-dark rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : medicines.length === 0 ? (
            <Card className="rounded-3xl shadow-nurture">
              <CardContent className="py-16 text-center">
                <Pill className="w-16 h-16 mx-auto text-nurture-muted/30 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-nurture-navy mb-2">No medicines tracked</h3>
                <p className="text-nurture-muted">Track your family's medications and refills</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {medicines.map((med) => {
                  const needsRefillSoon = med.refill_date && new Date(med.refill_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                  return (
                    <motion.div
                      key={med.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                    >
                      <Card className={`rounded-3xl shadow-nurture group ${needsRefillSoon ? 'border-amber-300 border-2' : ''}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${needsRefillSoon ? 'bg-amber-100' : 'bg-blue-100'}`}>
                                <Pill className={`w-5 h-5 ${needsRefillSoon ? 'text-amber-600' : 'text-blue-600'}`} />
                              </div>
                              <div>
                                <h3 className="font-heading font-bold text-nurture-navy">{med.medicine_name}</h3>
                                <p className="text-sm text-nurture-muted">{med.member_name}</p>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {med.dosage && <Badge variant="outline">{med.dosage}</Badge>}
                                  {med.frequency && <Badge variant="outline">{med.frequency}</Badge>}
                                </div>
                                {med.refill_date && (
                                  <p className={`text-sm mt-2 ${needsRefillSoon ? 'text-amber-600 font-medium' : 'text-nurture-muted'}`}>
                                    Refill: {format(new Date(med.refill_date), 'MMM d, yyyy')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteMedicine(med.id)}
                              className="opacity-0 group-hover:opacity-100 text-nurture-muted hover:text-red-500"
                              data-testid={`delete-med-${med.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AppointmentsPage;
